import React, { memo, useMemo } from 'react';
import { Container } from '@common/components/container';
import { useAppSelector } from '@common/hooks';
import { selected } from '@modules/organisation/selectors/organisation';
import { useTranslation } from 'react-i18next';
import { useRequest } from '@common/services/api';
import Overview from '@common/components/overview';
import DataTable from '@common/components/datatable';
import Placeholder from '@common/components/placeholder';
import useColumns from './use-columns';
import { EvaluationsMeta } from '@modules/performance/types';
import { omit } from 'lodash';
import { useHistory, useParams } from 'react-router';
import { DataTableRow } from '@common/components/datatable/types';

interface UserProfileEvaluationsProps {
  isOwnProfile?: boolean;
}

const UserProfileEvaluations = ({ isOwnProfile = false }: UserProfileEvaluationsProps): React.ReactElement => {
  const { t } = useTranslation();
  const org = useAppSelector(selected);
  const { userId } = useParams<{ userId: string }>();
  const loggedInUser = useAppSelector((state) => state.loggedUser.user);
  const params = useParams<{ networkId: string }>();
  const history = useHistory();

  const apiConfig = {
    useCursor: true,
    limit: 20,
    transform: (res: any) => {
      return {
        ...res,
      };
    },
  };

  const apiUrl = useMemo(() => {
    if (isOwnProfile) {
      return `/v1/organisations/${org.id}/users/me/performance/evaluations`;
    }
    if (userId) {
      return `/v1/organisations/${org.id}/users/${userId}/performance/evaluations`;
    }
    return `/v1/organisations/${org.id}/users/${loggedInUser.id}/performance/evaluations`;
  }, [org.id, userId, loggedInUser.id, isOwnProfile]);

  const {
    data: evaluations,
    meta: metaData,
    loading,
    refetch,
  } = useRequest(apiUrl, apiConfig);

  const columns = useColumns();
  const basePath = params.networkId
  ? `/networks/${params.networkId}`
  : '/admin';

  const handleRowClick = (row: DataTableRow) => {
    history.push(`${basePath}/performance/evaluations/${row.id}`);
  };

  const meta: EvaluationsMeta = metaData;

  return (
    <Container.Content>
      <Overview>
        <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-200">
          <DataTable
            columns={columns}
            data={evaluations || []}
            loading={loading}
            onShowMore={refetch}
            onRowClick={handleRowClick}
            pagination={{
              ...omit(apiConfig, 'payload'),
              ...meta?.pagination,
            }}
            emptyView={
              <Placeholder
                title={t('performance:no_evaluations_placeholder')}
              />
            }
          />
        </div>
      </Overview>
    </Container.Content>
  );
};

const MemoizedUserProfileEvaluations = memo<UserProfileEvaluationsProps>(UserProfileEvaluations);

export default MemoizedUserProfileEvaluations;
